import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import { toast } from "react-toastify";

import { addCoupon, addGiftCard, removeCoupon } from "../../../core/checkout.core";
import { formatCurrency } from "../../../core/currency.core";
import { useTranslation } from "../../../core/translation.core";
import useCheckoutTotal from "../../../hooks/services/useCheckoutTotal.hook";
import { useCalculateTotal } from "../../../hooks/services/useCalculateTotal.hook";
import { usePosCheckout } from "../../../hooks/services/useFetchCheckout";
import useRetailShop from "../../../hooks/useRetailShop";
import posIcons from "../../../icons/pos.icons";
import XMarkIcon from "../../../icons/xMark.icon";
import { Cart } from "../../../types/cart";
import { FancyLabelAndInput, FancyTextInput } from "../../reuseables/form.comp";
import { PosModal } from "../../reuseables/modal.comp";
import InfoIcon from "../../../icons/info.icon";
import rewardCore from "../../../core/reward.core";
import { Reward } from "../../../types/reward";
import { format } from "date-fns";
import { CheckoutTotal } from "../../../types/checkout";
import localeCore from "../../../core/locale.core";
import CombineOrder from "../combineOrder.comp";
import Cookies from "js-cookie";
import useAdminShop from "../../../hooks/useAdminShop";
import giftCardCore from "../../../core/giftCard.core";
import { roundAmt } from "../../../core/round.core";
import { ShopFull } from "../../../types/retailShop";

interface CartFooterProps {
  cart: Cart;
  userId: string;
  checkoutFn: () => void;
  onlineOrderFn: () => void;
}

export function CartFooter({ cart, userId, checkoutFn, onlineOrderFn }: CartFooterProps) {
  const { t, msg } = useTranslation();
  const sessionId = Cookies.get("sessionId");
  const shop = useAdminShop();
  const retailShop = useRetailShop();
  const calculateTotalMutation = useCalculateTotal();

  const methods = useForm<any>();
  const orderOverrideDiscount = useWatch({
    control: methods.control,
    name: "orderOverrideDiscount",
  });

  const {
    checkoutTotal, //
    rewardValue,
    useRewardPoints,
    setUseRewardPoints,
    isRevalidating,
  } = useCheckoutTotal({
    cart: cart,
    orderOverrideDiscount: orderOverrideDiscount,
    userId: userId,
  });

  const items = cart?.items.map((c) => {
    let attachToIdx = cart.items.findIndex((ci) => ci.id === c.attachCartItemId);

    return {
      attachToIdx: attachToIdx === -1 ? undefined : attachToIdx,
      customization: c.customization,
      overridePrice: c.overridePrice,
      price: c.itemPrice * c.quantity,
      productId: c.product.id,
      productNote: c.productNote,
      quantity: c.quantity,
      topUps: c.topUps,
      warehouseId: c.warehouseId,
    };
  });

  const triggerRecalculation = () => {
    if (items && items.length > 0 && userId) {
      calculateTotalMutation.mutate({
        items,
        userId,
        usedReward: rewardValue,
        orderOverrideDiscount,
      });
    }
  };

  // State for managing visibility of modals
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showRewardsModal, setShowRewardsModal] = useState(false);

  const handleShowCouponModal = () => {
    if (!userId) {
      toast.error(t(msg.notifications.selectCustomerFirst));
      return;
    }

    if (cart.items.length < 1) {
      toast.error(t(msg.cart.pleaseAddProductToCart));
      return;
    }

    // @ts-ignore
    if (checkoutTotal.orderOverrideDiscount > 0 && !retailShop.allowCouponWithPriceOverride) {
      toast.error(t(msg.cart.noCouponOnOverride));
      return;
    }
    setShowCouponModal(true);
  };

  const handleShowGiftCardModal = () => {
    if (!userId) {
      toast.error(t(msg.notifications.selectCustomerFirst));
      return;
    }

    if (cart.items.length < 1) {
      toast.error(t(msg.cart.pleaseAddProductToCart));
      return;
    }

    // @ts-ignore
    if (checkoutTotal.orderOverrideDiscount > 0 && !(retailShop?.allowCouponWithPriceOverride ?? false)) {
      toast.error(t(msg.cart.noGiftCardOnOverride));
      return;
    }
    setShowGiftCardModal(true);
  };

  return (
    <>
      <section className="py-8 w-full border-t">
        <div className="px-5 space-y-6">
          <section className="bg-[#EBF2FD] dark:bg-her-backblue py-2 px-4 block lg:flex-btw rounded-md font-bold">
            <h1 className="hidden lg:block">Add</h1>
            <div className="text-[#3880E8CC] space-x-4 cursor-pointer text-sm">
              {checkoutTotal && (
                <span id="coupon-link" className="hover:underline" onClick={handleShowCouponModal}>
                  {t(msg.checkout.coupon)}
                </span>
              )}

              <span className="hover:underline" onClick={handleShowGiftCardModal}>
                {t(msg.checkout.giftCard)}
              </span>

              <span
                id="order-discount-link"
                className="hover:underline"
                onClick={() => {
                  // toggle isorderdiscount
                  setShowDiscount(!showDiscount);
                }}
              >
                {t(msg.checkout.orderDiscount)}
              </span>
            </div>
          </section>

          {showDiscount && <OverrideOrderPanel methods={methods} checkoutTotal={checkoutTotal} shop={shop} />}

          {/* Display of Subtotal */}
          <div className="flex flex-col space-y-2">
            <div className="flex justify-between" id="pos-cart-subtotal" data-value={checkoutTotal?.itemsTotal}>
              <span>{t(msg.pos.subTotal)}</span>

              <span className="text-base">
                <AmtSkeleton isLoading={isRevalidating} value={checkoutTotal?.itemsTotal} currency={checkoutTotal?.currency} />
              </span>
            </div>
            <div className="flex justify-between">
              <CartRewardPointsRow
                checkoutTotal={checkoutTotal}
                useRewardPoints={useRewardPoints}
                setUseRewardPoints={setUseRewardPoints}
                rewardValue={rewardValue}
                isRevalidating={isRevalidating}
                onOpenRewardsModal={() => setShowRewardsModal(true)}
              />
            </div>
            {/* @ts-ignore */}
            {checkoutTotal?.couponDiscount > 0 && (
              <div className="flex justify-between">
                <span className="">{t(msg.cart.couponDiscount)}</span>
                <div className="flex gap-2 items-center">
                  <AmtSkeleton
                    isLoading={isRevalidating}
                    // @ts-ignore
                    value={checkoutTotal?.couponDiscount * -1}
                    currency={checkoutTotal?.currency}
                  />
                </div>
              </div>
            )}

            {checkoutTotal && checkoutTotal?.giftCardDiscount > 0 && (
              <div className="flex justify-between">
                <span className="">{t(msg.cart.giftCardDiscount)}</span>
                <div className="flex gap-2 items-center">
                  <AmtSkeleton
                    isLoading={isRevalidating}
                    // @ts-ignore
                    value={checkoutTotal?.giftCardDiscount * -1}
                    currency={checkoutTotal?.currency}
                  />
                </div>
              </div>
            )}

            {checkoutTotal && checkoutTotal?.membershipDiscount.discount > 0 && checkoutTotal.orderOverrideDiscount <= 0 ? (
              <div className="flex justify-between">
                <span className="">{localeCore.getLocaleValueDefault(checkoutTotal.membershipDiscount.details?.name)}</span>
                <div className="flex gap-2 items-center">
                  <AmtSkeleton
                    isLoading={isRevalidating}
                    value={checkoutTotal?.membershipDiscount.discount * -1}
                    currency={checkoutTotal?.currency}
                  />
                </div>
              </div>
            ) : null}
            {checkoutTotal?.roundOffAdjustment && checkoutTotal?.roundOffAdjustment !== 0 ? (
              <div
                className="flex justify-between"
                id="rounding-adjustment"
                data-value={checkoutTotal?.roundOffAdjustment ? checkoutTotal?.roundOffAdjustment * -1 : undefined}
              >
                <span>{t(msg.pos.roundOffDeduct)}</span>
                <div className="flex gap-2 items-center">
                  <AmtSkeleton
                    isLoading={isRevalidating}
                    // @ts-ignore
                    value={checkoutTotal?.roundOffAdjustment * -1}
                    currency={checkoutTotal?.currency}
                  />
                </div>
              </div>
            ) : null}
            {/* @ts-ignore */}
            {checkoutTotal?.orderOverrideDiscount > 0 ? (
              <div className="flex justify-between">
                <span className="">{t(msg.cart.priceOverrideDiscount)}</span>
                <div className="flex gap-2 items-center">
                  <AmtSkeleton
                    isLoading={isRevalidating}
                    //  @ts-ignore
                    value={checkoutTotal?.orderOverrideDiscount * -1}
                    currency={checkoutTotal?.currency}
                  />
                </div>
              </div>
            ) : null}
          </div>

          {/* Display of Payable Amount */}
          <div className="flex justify-between font-bold" data-value={checkoutTotal?.total} id="cart-total">
            <span>{t(msg.pos.payableAmount)}</span>

            <div className="flex gap-2 items-center">
              <AmtSkeleton isLoading={isRevalidating} value={checkoutTotal?.total} currency={checkoutTotal?.currency} />
            </div>
          </div>

          {/* Checkout Buttons */}
          <div>
            <div className="flex justify-end mb-2">
              {shop?.posOnlineOrderEnabled ? (
                <button
                  id="pos-online-order-btn"
                  onClick={onlineOrderFn}
                  className="flex-center  gap-2 text-her-blue py-3 px-2  disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                  <span>{t(msg.pos.placeAsOnlineOrder)}</span>
                  <span>{posIcons.bagIcon}</span>
                </button>
              ) : null}
              {shop?.combineOrderEnabled ? <CombineOrder cart={cart} userId={userId} sessionId={sessionId as string} /> : null}
            </div>

            <button
              id="pos-checkout-btn"
              onClick={checkoutFn}
              className="flex-center shadow-sm w-full gap-2 bg-green-600 hover:bg-green-700 text-white py-3 px-2 rounded-lg  disabled:bg-gray-500 disabled:cursor-not-allowed"
            >
              <span>{posIcons.proceedCartIcon}</span>
              <span>{t(msg.pos.placeAsPosOrder)}</span>
            </button>
          </div>
        </div>
      </section>

      {/* Modals for Discount and Coupon*/}
      {showCouponModal ? (
        <CouponModal setShowCouponModal={setShowCouponModal} userId={userId} onSuccess={triggerRecalculation} />
      ) : null}
      {showGiftCardModal ? (
        <GiftCardModal setShowGiftCardModal={setShowGiftCardModal} userId={userId} onSuccess={triggerRecalculation} />
      ) : null}
      {showRewardsModal ? (
        //  @ts-ignore
        <RewardsModal setShowRewardsModal={setShowRewardsModal} userId={userId} checkoutTotal={checkoutTotal} />
      ) : null}
    </>
  );
}

function CartRewardPointsRow({
  checkoutTotal,
  useRewardPoints,
  setUseRewardPoints,
  rewardValue,
  isRevalidating,
  onOpenRewardsModal,
}: {
  checkoutTotal: CheckoutTotal | undefined;
  useRewardPoints: number;
  setUseRewardPoints: Dispatch<SetStateAction<number>>;
  rewardValue: number;
  isRevalidating: boolean;
  onOpenRewardsModal: () => void;
}) {
  const { t, msg } = useTranslation();

  if (!checkoutTotal || !(checkoutTotal.userRewardPoints > 0 && checkoutTotal.orderOverrideDiscount <= 0)) {
    return null;
  }

  const cap =
    typeof checkoutTotal.redeemableRewardTotal === "number" && checkoutTotal.redeemableRewardTotal >= 0
      ? checkoutTotal.redeemableRewardTotal
      : 0;

  const commitPoints = (raw: string) => {
    if (raw.trim() === "") {
      setUseRewardPoints(0);
      return;
    }
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return;
    setUseRewardPoints(Math.min(Math.max(0, n), cap));
  };

  return (
    <div className="flex justify-between w-full items-start">
      <div className="flex items-start gap-1 min-w-0">
        <div className="min-w-0">
          <label className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {t(msg.checkout.useRewardPoints)}
              <input
                id="pos-reward-points-input"
                type="number"
                min={0}
                max={cap}
                step="any"
                className="w-24 border rounded px-1 py-0.5 text-sm bg-white dark:bg-gray-900"
                value={Number.isFinite(useRewardPoints) ? useRewardPoints : 0}
                onChange={(e) => commitPoints(e.target.value)}
              />
              <span id="pos-reward-redeemable-max" className="sr-only" data-redeemable-max={cap} />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={cap <= 0}
                  id="pos-reward-points-use-max"
                  className="text-sm text-[#3880E8CC] hover:underline disabled:opacity-40 disabled:no-underline"
                  onClick={() => setUseRewardPoints(cap)}
                >
                  {t(msg.checkout.useMaxRewardPoints)}
                </button>
                <button
                  type="button"
                  disabled={!Number.isFinite(useRewardPoints) || useRewardPoints <= 0}
                  id="pos-reward-points-clear"
                  className="text-sm text-[#3880E8CC] hover:underline disabled:opacity-40 disabled:no-underline"
                  onClick={() => setUseRewardPoints(0)}
                >
                  {t(msg.checkout.clearRewardPoints)}
                </button>
              </div>
            </span>
          </label>
          <span className="text-xs block mt-1">Total Reward: {checkoutTotal.userRewardPoints}</span>
        </div>
        <button type="button" onClick={onOpenRewardsModal} aria-label="Rewards info">
          <InfoIcon className=" w-5 h-5" />
        </button>
      </div>

      <div id="pos-reward-discount-line" data-value={rewardValue > 0 ? rewardValue * -1 : undefined} className="shrink-0">
        <AmtSkeleton isLoading={isRevalidating} value={rewardValue * -1} currency={checkoutTotal?.currency} />
      </div>
    </div>
  );
}

const AmtSkeleton = ({ isLoading, value, currency }) => {
  const pos = useRetailShop();

  if (isLoading) {
    return <div className="items-center bg-gray-200 shadow-md animate-pulse w-16 h-4"></div>;
  }

  return <>{formatCurrency(value, currency ?? pos?.defaultCurrency)}</>;
};

const OverrideOrderPanel = ({
  methods,
  checkoutTotal,
  shop,
}: {
  methods: UseFormReturn<any>;
  checkoutTotal: CheckoutTotal | undefined;
  shop: ShopFull | undefined;
}) => {
  const { t, msg } = useTranslation();
  const { setValue, control } = methods;

  const [orderDiscountPrice, orderDiscountPercentage] = useWatch({
    control,
    name: ["orderDiscountPrice", "orderDiscountPercentage"],
  });

  useEffect(() => {
    if (!checkoutTotal?.itemsTotal) return;

    if (!orderDiscountPrice && !orderDiscountPercentage) {
      setValue("orderOverrideDiscount", undefined);
      return;
    }

    if (orderDiscountPrice) {
      setValue(
        "orderOverrideDiscount",
        // convert to number to handle "100." case
        Number(orderDiscountPrice)
      );
    } else if (orderDiscountPercentage) {
      const discountAmount = (checkoutTotal.itemsTotal * orderDiscountPercentage) / 100;
      const roundedDiscountAmount = roundAmt(discountAmount, shop!.posOrderRoundOff ?? shop!.orderRoundOff);
      setValue("orderOverrideDiscount", roundedDiscountAmount);
    }
  }, [checkoutTotal?.itemsTotal, setValue, orderDiscountPrice, orderDiscountPercentage, shop]);

  const resetOrderDiscount = () => {
    setValue("orderOverrideDiscount", 0);
    setValue("orderDiscountPrice", null);
    setValue("orderDiscountPercentage", null);
  };

  return (
    <section className="space-y-3">
      <div className="gap-2 flex">
        <FancyTextInput
          register={() => methods.register("orderDiscountPercentage")}
          type="number"
          placeholder={t(msg.formLabels.orderDiscountPercentage)}
        />
        <FancyTextInput
          register={() => methods.register("orderDiscountPrice")}
          placeholder={t(msg.formLabels.orderDiscountPrice)}
        />
      </div>

      {(orderDiscountPrice || orderDiscountPercentage) && (
        <div className="flex justify-end">
          <button
            onClick={resetOrderDiscount}
            className="bg-red-600 px-5 text-white rounded text-center"
            id="reset-order-discount-btn"
          >
            {t(msg.form.reset)}
          </button>
        </div>
      )}
    </section>
  );
};

const CouponModal = ({ setShowCouponModal, userId, onSuccess }) => {
  const { t, msg } = useTranslation();
  const couponBtnRef = useRef<HTMLDivElement>(null);
  const methods = useForm<{ couponCode: String }>();

  const onSubmit = async (data: { couponCode: String }) => {
    try {
      const res = await addCoupon(data.couponCode, userId);

      if (res.status >= 300) {
        if (res.data?.msg === "notExists") {
          toast.error(t(msg.cart.couponInvalid));
          return;
        }

        if (res.data?.msg === "couponTooLarge") {
          toast.error(t(msg.cart.couponTooLarge));
          return;
        }

        toast.error(t(msg.errors.contactAdmin));
        return;
      }

      onSuccess();
      setShowCouponModal(false);
    } catch (error) {
      toast.error(t(msg.errors.contactAdmin));
    }
  };

  return (
    <PosModal setModalOpened={setShowCouponModal} btnRef={couponBtnRef}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="mx-8">
        <h1 className="text-2xl">{t(msg.checkout.couponCode)}</h1>
        <div className="mb-8">
          <FancyLabelAndInput
            type="text" //
            methods={methods}
            name="couponCode"
            label=""
            placeholder={t(msg.cart.enterCoupon)}
          />
        </div>
        <SelectedCouponsList userId={userId} onSuccess={onSuccess} />
        <button
          id="coupon-btn"
          className="flex-center w-full gap-1 bg-her-blue text-white py-3 mb-2 px-2 rounded-lg text-lg disabled:bg-gray-500 disabled:cursor-not-allowed"
        >
          <span>{t(msg.form.add)}</span>
        </button>
      </form>
    </PosModal>
  );
};

export function SelectedCouponsList({ userId, onSuccess }: { userId: string; onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  const { data: checkout } = usePosCheckout();

  const handleRemove = async (id: string) => {
    await removeCoupon(id, userId);
    await queryClient.invalidateQueries({ exact: false, queryKey: ["posCheckout"] });
    if (onSuccess) {
      onSuccess();
    }
  };

  if (!checkout) return null;
  return (
    <div className="flex flex-wrap mb-8 gap-2">
      {checkout.coupons.map((c) => (
        <div key={c.id} className="flex border border-her-blue p-3 rounded-md">
          <p className="mr-6">{c.code}</p>
          <button
            onClick={() => handleRemove(c.id)}
            type="button"
            className="border rounded-2xl border-her-blue hover:bg-her-blue hover:text-white"
          >
            <XMarkIcon />
          </button>
        </div>
      ))}
    </div>
  );
}

const GiftCardModal = ({
  setShowGiftCardModal,
  userId,
  onSuccess,
}: {
  setShowGiftCardModal: Dispatch<SetStateAction<boolean>>;
  userId: string;
  onSuccess: () => void;
}) => {
  const { t, msg } = useTranslation();
  const couponBtnRef = useRef(null);
  const methods = useForm<{ giftCardCode: string }>();

  const onSubmit = async (data: { giftCardCode: string }) => {
    try {
      const res = await addGiftCard(data.giftCardCode, userId);

      // Check for error status
      if (res.status >= 300) {
        // Handle specific error messages
        if (res.data?.msg === "invalidCode" || res.data?.msg === "usedCode" || res.data?.msg === "expiredCode") {
          toast.error(t(msg.cart.giftCardInvalid));
          return;
        }

        if (res.data?.msg === "discountTooLarge") {
          toast.error(t(msg.cart.giftCardTooLarge));
          return;
        }

        // Handle any other error
        toast.error(t(msg.errors.contactAdmin));
        return;
      }

      // Success case
      onSuccess();
      setShowGiftCardModal(false);
    } catch (error) {
      // Handle network errors or exceptions
      toast.error(t(msg.errors.contactAdmin));
    }
  };

  const onRedeem = async (data: { userId: string; code: string }) => {
    const res = await giftCardCore.redeemAsReward(data);
    if (res?.msg === "invalidCode" || res?.msg === "usedCode" || res?.msg === "expiredCode") {
      toast.error(t(msg.cart.giftCardInvalid));
      return;
    }

    onSuccess();
    setShowGiftCardModal(false);
  };

  return (
    //  @ts-ignore
    <PosModal setModalOpened={setShowGiftCardModal} btnRef={couponBtnRef}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="mx-8">
        <h1 className="text-2xl">{t(msg.checkout.giftCard)}</h1>
        <div className="mb-8">
          <FancyLabelAndInput
            type="text" //
            methods={methods}
            name="giftCardCode"
            label=""
            placeholder={t(msg.cart.enterGiftCard)}
          />
        </div>
        <SelectedCouponsList userId={userId} />
        <button
          type="button"
          id="coupon-btn"
          onClick={() => onRedeem({ userId, code: methods.getValues("giftCardCode") })}
          className="flex-center w-full gap-1 border-2 border-her-blue bg-white text-her-blue py-3 mb-2 px-2 rounded-lg text-lg disabled:bg-gray-500 disabled:cursor-not-allowed"
        >
          <span>{t(msg.giftCard.redeemAsReward)}</span>
        </button>
        <button
          id="coupon-btn"
          className="flex-center w-full gap-1 bg-her-blue text-white py-3 mb-2 px-2 rounded-lg text-lg disabled:bg-gray-500 disabled:cursor-not-allowed"
        >
          <span>{t(msg.form.add)}</span>
        </button>
      </form>
    </PosModal>
  );
};

// Rewards Modal Component
interface RewardsModalProps {
  setShowRewardsModal: Dispatch<SetStateAction<boolean>>;
  userId: string;
  checkoutTotal: CheckoutTotal;
}

const RewardsModal = ({ setShowRewardsModal, userId, checkoutTotal }: RewardsModalProps) => {
  const rewardBtnRef = useRef(null);

  const { data } = useQuery({
    queryFn: () => rewardCore.getRewards(userId),
    queryKey: ["Pos Rewards"],
  });

  return (
    //  @ts-ignore
    <PosModal setModalOpened={setShowRewardsModal} btnRef={rewardBtnRef}>
      <section className="px-4 w-full">
        <h1 className="text-2xl mb-6">User Rewards</h1>
        {/* @ts-ignore */}
        <RewardsList rewards={data} />
        <p>Available Reward: {checkoutTotal.userRewardPoints}</p>
        <p>Redeemable Reward: {checkoutTotal.redeemableRewardTotal}</p>
        <p>Current Order Reward: {checkoutTotal.orderRewardPoints}</p>
      </section>
    </PosModal>
  );
};

const RewardsList = ({ rewards }: { rewards: Reward[] }) => {
  if (!rewards) return null;

  return (
    <section className="mb-6">
      <div className="grid grid-cols-3">
        <p>Date</p>
        <p>Points</p>
        <p>Status</p>
      </div>

      {rewards.map((r) => (
        <div key={r.id} className="grid grid-cols-3">
          <p>{format(new Date(r.orderDate), "yyyy-MM-dd")}</p>
          <p>{r.points}</p>
          <p>{r.status}</p>
        </div>
      ))}
    </section>
  );
};
